import { CommonModule, Location } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { DrawerModule } from '@gci/components/drawer/drawer.module';
import { ToastService } from '@gci/components/toast/toast.service';
import { IDevice } from 'app/models/devices.type';
import { IGroup } from 'app/models/group.type';
import { DevicesService } from 'app/modules/admin/services/devices.service';
import { GroupService } from 'app/modules/admin/services/group.service';
import { SharedModule } from 'app/modules/admin/shared.module';
import { IAlertRulesFeature, IDeviceFeature, IGroupFeature, IMemberFeature, RoleNameType } from 'app/models/role.constant';
import { FeatureAccessService } from 'app/modules/admin/services/feature-access.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DialogComponent } from '../dialog/dialog.component';
import { AuthService } from 'app/core/auth/auth.service';
import { BaseComponent } from '@gci/components/base-page/base-component';
import { TranslocoService } from '@jsverse/transloco';
import { DeviceAddFormComponent } from "../../devices-page/device-add-form/device-add-form.component";
import { GroupAddFormComponent } from "../groups/group-add-form/group-add-form.component";

export type TButtonList =
    'show_group_id' | 
    'edit_group' |
    'leave_group' |
    'delete_group' |
    'edit_device' |
    'delete_device' | 
    'add_device' |
    'filter_device' |
    'edit_rule' |
    'delete_rule' |
    'add_rule' |
    'filter_rule' |
    'edit_role' |
    'remove_user' |
    'invite_member' |
    'filter_member' |
    'activate_rule' |
    'deactivate_rule'


@Component({
    selector: 'app-dropdown-menu',
    templateUrl: './dropdown-menu.component.html',
    standalone: true,
    exportAs: 'dropdown-menu',
    imports: [
        CommonModule,
        SharedModule,
        MatMenuModule,
        DrawerModule,
        GroupAddFormComponent
    ]
})
export class DropdownMenuComponent extends BaseComponent implements OnInit {

    // -----------------------------------------------------------------------------------------------------
    // @ Properties
    // -----------------------------------------------------------------------------------------------------
    @Input() activeGroup!: IGroup;
    @Input() data?: IGroup;
    @Input() dataDevice?: IDevice;
    // @Input() type: 'group' | 'device' | '' = '';
    @Input() buttonList: TButtonList[] = [];
    @Input() buttonStyle: string = '';
    @Input () backToMenu: boolean = false;
    @Input() icon: string = 'heroicons_outline:ellipsis-vertical';
    @Input() iconCustomClass: string = '';

    @Output() buttonClickHandle: EventEmitter<TButtonList> = new EventEmitter<TButtonList>();

    public drawerOpen: boolean = false;
    public hasClipboardText: boolean = false;
    private _groupService = inject(GroupService);
    private _deviceService = inject(DevicesService);
    private _toastService = inject(ToastService);
    private _authService = inject(AuthService);
    private location = inject(Location);
    private dialogConfig = new MatDialogConfig();
    readonly dialog = inject(MatDialog);


    // -----------------------------------------------------------------------------------------------------
    // @ Constructor
    // -----------------------------------------------------------------------------------------------------
    constructor(
        private _access: FeatureAccessService,
        private _translocoService: TranslocoService
    ) {
        super("DropdownMenuComponent", _translocoService)
        this.dialogConfig.width = "500px";
        this.dialogConfig.enterAnimationDuration = "50ms";
        this.dialogConfig.exitAnimationDuration = "50ms";
    }


    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle Hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On Init
     */
    override ngOnInit(): void {
        super.ngOnInit()
    }

    get GroupAlias() {
        if (this._authService.currentUser?.tenant?.alias) {
            return this._authService.currentUser?.tenant?.alias;
        }
        return 'Group'
    }


    // -----------------------------------------------------------------------------------------------------
    // @ Methods
    // -----------------------------------------------------------------------------------------------------
    isShow(button_name: TButtonList): boolean {
        return this.buttonList.includes(button_name);
    }

    buttonClick(butonType: TButtonList) {
        this.buttonClickHandle.emit(butonType);
    }

    onCancel() {
        this.drawerOpen = false;
    }

    onEdit() {
        this.drawerOpen = true;
    }

    onLeaveGroup(type: 'Leave' | 'Delete') {

        this.dialogConfig.data = {
            title: `${type} Group`,
            description: `Are you sure you want to ${type.toLowerCase()} the group?`
        };

        const dialogRef = this.dialog.open(DialogComponent, this.dialogConfig);

        dialogRef.afterClosed().subscribe(validation => {
            if(validation){
                if(type === 'Delete'){
                    this._groupService.deleteGroup().subscribe({
                        next: res => {
                            this._toastService.success(`You deleted group ${res.alias}`);
                        },
                        error: err => {
                            this._toastService.error(err);
                        }
                    });
                }else {
                    this._authService.currentUser && this._groupService.leaveGroup(this._authService.currentUser.id).subscribe({
                        next: res => {
                            this._toastService.success(`You left group `);
                        },
                    })
                }
            }
        })
    }

    onDeleteDevice() {

        this.dialogConfig.data = {
            title: "Delete Device",
            description: "Are you sure you want to delete this device?"
        };

        const dialogRef = this.dialog.open(DialogComponent, this.dialogConfig);

        dialogRef.afterClosed().subscribe(validation => {
            if(validation){
                if (this.dataDevice) {
                    this._deviceService.removeDevice(this.dataDevice.id, this.dataDevice.group_id || this.activeGroup.id).subscribe({
                        next: res => {
                            const toastSuccessText = this._translocoService.translate('deleteDevice', {name: res.name});
                            this._toastService.success(toastSuccessText);
                            this.backToMenu && this.location.back();
                        },
                        error: err => {
                            this._toastService.error(err);
                        }
                    });
                }
            }
        })
    }

    onShowGroupID() {
        this.dialogConfig.data = {
            title: this.GroupAlias+" ID",
            description: "Use this ID to let others join the "+this.GroupAlias.toLowerCase(),
            type: "show",
            id: this.activeGroup.id
        };

        this.dialog.open(DialogComponent, this.dialogConfig);
        
    }

    canAccessGroup(fiture: keyof (IGroupFeature)): boolean {
        let role: RoleNameType = this.activeGroup?.role_name || 'read-only';
        return this._access.isAllowAccessGroupFeatures(fiture, role);
    }

    canAccessDevice(fiture: keyof (IDeviceFeature)): boolean {
        let role: RoleNameType = this.activeGroup?.role_name || 'read-only';
        return this._access.isAllowAccessDeviceFeatures(fiture, role);
    }

    canAccessAlertRule(fiture: keyof (IAlertRulesFeature)): boolean {
        let role: RoleNameType = this.activeGroup?.role_name || 'read-only';
        return this._access.isAllowAccessAlertRulesFeature(fiture, role);
    }

    canAccessMember(fiture: keyof (IMemberFeature)): boolean {
        let role: RoleNameType = this.activeGroup?.role_name || 'read-only';
        return this._access.isAllowAccessMemberFeature(fiture, role);
    }
}
